import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { errMsg, useApi, useMe } from "../../lib/api";
import { PreviewViewer } from "../../components/WebsitePreview";
import { CustomiseBar } from "../../components/CustomiseBar";
import { UnderstandingCard } from "../../components/UnderstandingCard";
import { ContentBar } from "../../components/ContentBar";
import { ContentEditor } from "../../components/ContentEditor";
import { useContentEditor } from "../../components/useContentEditor";
import { ImagesBar, useAiImages } from "../../components/AiImages";

export default function PreviewPage() {
  const { pid } = useParams();
  const api = useApi();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [bar, setBar] = useState(0);
  const load = () => api.get(`/projects/${pid}/design`).then((r) => setSt(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, [api, pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = st?.versions?.find((v) => v.id === st.current_id);
  const onImgs = useCallback(() => { api.get(`/projects/${pid}/design`).then((r) => { setSt(r.data); setSpec(null); }); }, [api, pid]);
  const [spec, setSpec] = useState(null);
  const v2 = useMe()?.me?.builder_v2;
  const ai = useAiImages({ api, pid, onDone: onImgs, enabled: !!(shown && v2 && !st?.design_approved) });
  const [, setRev] = useState(0);
  const [editing, setEditing] = useState(false);
  const [tools, setTools] = useState(() => window.innerWidth >= 1024);
  const live = spec || shown?.spec;
  const canEdit = shown && v2 && !st.design_approved;
  const ce = useContentEditor({ api, pid, spec: live, setSpec: (s) => { setSpec(s); setRev((n) => n + 1); }, initialUndo: shown?.content_undo?.length || 0, initialLabel: shown?.content_undo?.slice(-1)[0]?.label || null });
  return (
    <div className="min-h-screen bg-[#f4efe2] p-2 sm:p-4" data-testid="preview-page">
      <div className="max-w-[1600px] mx-auto space-y-2">
        <div className="flex items-center justify-between gap-2 px-1"><span className="font-display text-lg text-[#062a20]">Altiora</span><a href={`/build/${pid}?step=5`} className="text-xs underline text-slate-600" data-testid="preview-back-link">Back to my quotation</a></div>
        <p className="text-xs text-slate-500 px-1" data-testid="preview-page-label">Website preview — this is the same design your final website is built from.</p>
        {err && <p className="text-sm text-red-700" data-testid="preview-page-error">{err}</p>}
        {!st && !err && <p className="text-sm text-slate-500">Loading preview…</p>}
        {st && !shown && <p className="text-sm text-slate-600" data-testid="preview-page-empty">No preview yet. Create one from your quotation page.</p>}
        {canEdit && <button onClick={() => setTools((x) => !x)} className="lg:hidden w-full card-lux !p-3 text-sm font-semibold text-[#062a20] flex items-center justify-between" aria-expanded={tools} data-testid="preview-tools-toggle">{tools ? "Hide customisation tools" : "Customise design, text, photos & AI images"}<span aria-hidden>{tools ? "▲" : "▼"}</span></button>}
        <div className={tools ? "space-y-2" : "hidden"} data-testid="preview-tools">
        {canEdit && <CustomiseBar key={bar} api={api} pid={pid} aiMax={st.ai_max ?? 3} version={bar ? { ...shown, custom_undo: {}, ai_changes: shown.ai_changes } : shown} onSpec={(s) => { setSpec(s); setRev((n) => n + 1); }} />}
        {canEdit && <ContentBar ce={ce} editing={editing} setEditing={setEditing} onResetAll={() => setBar((n) => n + 1)} />}
        {canEdit && <ImagesBar ai={ai} api={api} />}
        {shown && v2 && <UnderstandingCard api={api} pid={pid} plan={live?.plan} sections={live?.content?.sections} locked={st.design_approved} onSaved={load} />}
        </div>
        {shown && <PreviewViewer spec={live} tall edit={canEdit && editing ? ce.ed : null} key={shown.id} />}
        {canEdit && <ContentEditor api={api} pid={pid} spec={live} ce={ce} ai={ai} />}
      </div>
    </div>);
}
